import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import ToolItem, { ItemTypes as ToolItemTypes } from './ToolItem';
import { EditIcon, DeleteIcon, DragHandleIcon, ChevronDownIcon } from '../ui/Icons';

const ItemTypes = { 
    TOOL: ToolItemTypes.TOOL,
    CATEGORY: 'category',
};

const ToolCategory = ({ category, tools, onEditCategory, onDeleteCategory, onEditTool, onDeleteTool, onMoveTool, viewMode, index, moveCategory, isOpen, onToggle }) => {
    const ref = useRef(null);

    const [, drop] = useDrop({
        accept: ItemTypes.CATEGORY,
        hover(item, monitor) {
            if (!ref.current) return;
            const dragIndex = item.index;
            const hoverIndex = index;
            if (dragIndex === hoverIndex) return;
            
            const hoverBoundingRect = ref.current?.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

            moveCategory(dragIndex, hoverIndex);
            item.index = hoverIndex;
        },
    });

    const [{ isDragging }, drag, preview] = useDrag({
        type: ItemTypes.CATEGORY,
        item: () => ({ id: category.id, index }),
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    preview(drop(ref));

    const [{ isOver }, toolDrop] = useDrop(() => ({
        accept: ItemTypes.TOOL,
        drop: (item) => onMoveTool(item.id, category.id),
        collect: (monitor) => ({
            isOver: !!monitor.isOver(),
        }),
    }));

    return (
        <div ref={ref} style={{ opacity: isDragging ? 0.2 : 1 }} className={`transition-colors`}>
            <div ref={toolDrop} className={`bg-black/20 p-4 rounded-lg ${isOver ? 'bg-white/10' : ''}`}>
                <div className="flex justify-between items-center group">
                    <button onClick={onToggle} className="flex items-center gap-2 flex-grow text-left">
                        <div ref={drag} className="cursor-move text-gray-500 hover:text-white transition-colors">
                            <DragHandleIcon size={20} />
                        </div>
                        <h3 className="text-lg font-bold text-white">{category.name}</h3>
                        <ChevronDownIcon size={20} className={`text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => onEditCategory(category)} className="p-1 hover:text-indigo-400"><EditIcon size={16}/></button>
                        <button onClick={() => onDeleteCategory(category.id)} className="p-1 hover:text-red-400"><DeleteIcon size={16}/></button>
                    </div>
                </div>

                <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isOpen ? 'max-h-[1000px] pt-4' : 'max-h-0'}`}>
                    <div className={viewMode === 'list' 
                        ? 'space-y-2' 
                        : 'grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-4'
                    }>
                        {tools.length > 0 ? (
                            tools.map(tool => (
                                <ToolItem key={tool.id} tool={tool} onEdit={onEditTool} onDelete={onDeleteTool} viewMode={viewMode} />
                            ))
                        ) : (
                            <p className="text-sm text-gray-500 col-span-full text-center py-4">Nenhuma ferramenta nesta categoria.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ToolCategory;